import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, sampleDocumentAPI, collaborationAPI } from '../services/api';
import ManageCollaborators from '../components/ManageCollaborators';
import PendingInvites from '../components/PendingInvites';
import './ProjectsDashboard.css';

const ProjectsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [sampleProject, setSampleProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', description: '' });
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [managingProject, setManagingProject] = useState(null);
  const [memberCounts, setMemberCounts] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    fetchPendingInvites();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await collaborationAPI.getPendingInvites();
      setPendingInvites(response.data.data.invites);
    } catch (err) {
      console.error('Failed to load pending invites:', err);
    }
  };

  const fetchMemberCount = async (projectId) => {
    try {
      const response = await collaborationAPI.getProjectMembers(projectId);
      return response.data.data.members.length;
    } catch (err) {
      return 1;
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const allProjects = response.data.data;

      // Separate sample project from user projects
      const sample = allProjects.find(p => p.isSample);
      const userProjects = allProjects.filter(p => !p.isSample);

      setSampleProject(sample);
      setProjects(userProjects);

      // Fetch member counts for all non-sample projects
      const counts = {};
      await Promise.all(
        userProjects.map(async (project) => {
          const count = await fetchMemberCount(project._id);
          counts[project._id] = count;
        })
      );
      setMemberCounts(counts);

      setLoading(false);
    } catch (err) {
      setError('Failed to load projects');
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (token) => {
    try {
      await collaborationAPI.acceptInvite(token);
      fetchPendingInvites();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvite = async (token) => {
    try {
      await collaborationAPI.declineInvite(token);
      fetchPendingInvites();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to decline invitation');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      const response = await projectsAPI.create(newProject);
      const createdProject = response.data.data;

      setProjects([createdProject, ...projects]);
      setShowCreateModal(false);
      setNewProject({ name: '', description: '' });

      // Navigate to discovery page for the new project
      navigate(`/projects/${createdProject._id}/discovery`);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create project');
    }

    setCreating(false);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setEditFormData({
      name: project.name,
      description: project.description || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError('');

    try {
      const response = await projectsAPI.update(editingProject._id, editFormData);
      const updatedProject = response.data.data;

      // Update the project in the list
      setProjects(projects.map(p =>
        p._id === updatedProject._id ? updatedProject : p
      ));

      setShowEditModal(false);
      setEditingProject(null);
      setEditFormData({ name: '', description: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update project');
    }

    setUpdating(false);
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await projectsAPI.delete(projectId);
      setProjects(projects.filter(p => p._id !== projectId));
    } catch (err) {
      setError('Failed to delete project');
    }
  };

  const handleViewProject = (projectId, isSample) => {
    if (isSample) {
      // Sample project is read-only, go directly to plan
      navigate(`/projects/${projectId}/plan`);
    } else {
      navigate(`/projects/${projectId}/discovery`);
    }
  };

  const handleViewPlan = (projectId) => {
    navigate(`/projects/${projectId}/plan`);
  };

  const handleViewSampleDocument = () => {
    setShowPdfModal(true);
  };

  const hasAccess = (project) => {
    if (!project.accessExpiryDate) return false;
    return new Date(project.accessExpiryDate) > new Date();
  };

  // Helper function to render project name with hyperlink for course
  const renderProjectName = (projectName) => {
    const courseText = 'Value Added Data Migration Course';
    if (projectName.includes(courseText)) {
      const parts = projectName.split(courseText);
      return (
        <>
          {parts[0]}
          <a
            href="https://academy.hopp.tech/products/courses/value-added-data-migration"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#3b82f6',
              textDecoration: 'underline',
              fontWeight: '600'
            }}
          >
            {courseText}
          </a>
          {parts[1]}
        </>
      );
    }
    return projectName;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="container py-4">
        <div className="dashboard-header">

        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {/* Pending Invites */}
        <PendingInvites
          invites={pendingInvites}
          onAccept={handleAcceptInvite}
          onDecline={handleDeclineInvite}
          loading={loading}
        />

        {/* Sample Project - Always show at top */}
        {sampleProject && (
          <div className="sample-project-section">
            <div className="sample-project-header">
              <h2>📝 Sample Project - See the Framework in Action</h2>
              <p>
                Explore a complete example of data migration project discovery work and formation of data migration plan.
								Download the professional project template bundle for $499.
              </p>

            </div>

            <div className="project-card sample-project-card">
              <div className="sample-badge-large">SAMPLE PROJECT</div>

              <div className="project-card-header">
                <div>
                  <h3>{renderProjectName(sampleProject.name)}</h3>
                  {sampleProject.description && (
                    <p className="project-description">{sampleProject.description}</p>
                  )}
                </div>
                <span className="status-badge status-completed">
                  Complete Example
                </span>
              </div>

              <div className="project-progress">
                <div className="progress-header">
                  <span>Completion</span>
                  <span className="progress-percentage">
                    {sampleProject.completionPercentage}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${sampleProject.completionPercentage}%` }}
                  ></div>
                </div>
              </div>

              {sampleProject.purchaseType === 'templates' && hasAccess(sampleProject) && (
                <div className="access-badge">
                  ✅ You have access to templates until{' '}
                  {new Date(sampleProject.accessExpiryDate).toLocaleDateString()}
                </div>
              )}

              <div className="project-meta">
                <span className="meta-item" style={{ fontWeight: 'bold', fontSize: '1.05em', color: '#2c3e50' }}>
                  📦 Includes: Complete Project Discovery + Project Plan + Data Migration Plan 1.0 + Policy + Risk Register + Cutover Runsheet + Checklist
                </span>
              </div>

              <div className="project-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleViewSampleDocument}
                >
                  View Sample Plan
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => handleViewProject(sampleProject._id, true)}
                >
                  View Full Discovery
                </button>
                {sampleProject.purchaseType === 'templates' && hasAccess(sampleProject) ? (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleViewPlan(sampleProject._id)}
                  >
                    Download Templates
                  </button>
                ) : (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleViewPlan(sampleProject._id)}
                  >
                    Get Templates ($499)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Section Divider */}
        <div className="section-divider"></div>

        {/* User Projects Section */}
        <div className="user-projects-section">
          <div className="section-header">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 className="section-title">My Projects</h2>
                <div className="info-icon-container">
                  <span className="info-icon">ℹ️</span>
                  <div className="info-tooltip">
                    <strong>Free Project Discovery</strong>
                    <p>Create unlimited projects and complete discovery at no cost.</p>
                    <strong>Automated Plan Generation</strong>
                    <p>$199/year for automated plan generation with 10 regenerations per project.</p>
                  </div>
                </div>
              </div>
              <p className="section-description">
                Conduct project discovery and create data migration plan for your projects.
              </p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              + New Project
            </button>
          </div>

          {projects.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-content">
                <h3>No Projects Yet</h3>
                <p>
                  Create your first project to start your project discovery at no cost
                  using the comprehensive framework.
                </p>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create Your First Project
                </button>
              </div>
            </div>
          ) : (
            <div className="projects-grid">
              {projects.map((project) => (
                <div key={project._id} className="project-card">
                  <div className="project-card-header">
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <h3>{project.name}</h3>
                        {memberCounts[project._id] > 1 && (
                          <span className="shared-badge" title={`${memberCounts[project._id]} team members`}>
                            👥 Shared
                          </span>
                        )}
                        <button
                          className="btn-icon"
                          onClick={() => handleEditProject(project)}
                          title="Edit project name and description"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            color: '#64748b',
                            fontSize: '14px'
                          }}
                        >
                          ✏️
                        </button>
                      </div>
                      {project.description && (
                        <p className="project-description">{project.description}</p>
                      )}
                      {memberCounts[project._id] > 1 && (
                        <p className="project-members-count">
                          You + {memberCounts[project._id] - 1} {memberCounts[project._id] - 1 === 1 ? 'other' : 'others'}
                        </p>
                      )}
                    </div>
                    <span className={`status-badge status-${project.status}`}>
                      {project.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="project-progress">
                    <div className="progress-header">
                      <span>Completion</span>
                      <span className="progress-percentage">
                        {project.completionPercentage}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${project.completionPercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {project.purchaseType === 'plan' && hasAccess(project) && (
                    <div className="access-badge">
                      ✅ Access until{' '}
                      {new Date(project.accessExpiryDate).toLocaleDateString()}
                    </div>
                  )}

                  <div className="project-meta">
                    <span className="meta-item">
                      Created: {new Date(project.createdAt).toLocaleDateString()}
                    </span>
                    <span className="meta-item">
                      Updated: {new Date(project.updatedAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="project-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewProject(project._id, false)}
                    >
                      {project.completionPercentage > 0 ? 'Continue' : 'Start'} Discovery
                    </button>
                    {project.completionPercentage > 0 && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleViewPlan(project._id)}
                      >
                        View Plan
                      </button>
                    )}
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setManagingProject(project)}
                    >
                      Manage
                    </button>
                    {project.isOwner && (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handleDeleteProject(project._id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Project</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateProject}>
              <div className="form-group">
                <label htmlFor="projectName" className="form-label">
                  Project Name *
                </label>
                <input
                  type="text"
                  id="projectName"
                  className="form-control"
                  value={newProject.name}
                  onChange={(e) =>
                    setNewProject({ ...newProject, name: e.target.value })
                  }
                  required
                  placeholder="e.g., Legacy ERP Migration"
                />
              </div>

              <div className="form-group">
                <label htmlFor="projectDescription" className="form-label">
                  Description (Optional)
                </label>
                <textarea
                  id="projectDescription"
                  className="form-control"
                  value={newProject.description}
                  onChange={(e) =>
                    setNewProject({ ...newProject, description: e.target.value })
                  }
                  placeholder="Brief description of your migration project"
                  rows="3"
                ></textarea>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && editingProject && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Project</h2>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleUpdateProject}>
              <div className="form-group">
                <label htmlFor="editProjectName" className="form-label">
                  Project Name *
                </label>
                <input
                  type="text"
                  id="editProjectName"
                  className="form-control"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  required
                  placeholder="e.g., Legacy ERP Migration"
                />
              </div>

              <div className="form-group">
                <label htmlFor="editProjectDescription" className="form-label">
                  Description (Optional)
                </label>
                <textarea
                  id="editProjectDescription"
                  className="form-control"
                  value={editFormData.description}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, description: e.target.value })
                  }
                  placeholder="Brief description of your migration project"
                  rows="3"
                ></textarea>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updating}
                >
                  {updating ? 'Updating...' : 'Update Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPdfModal && (
        <div className="modal-overlay" onClick={() => setShowPdfModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              width: '1200px',
              height: '90vh',
              padding: '0',
              overflow: 'hidden'
            }}
          >
            <div className="modal-header" style={{ padding: '1rem 1.5rem' }}>
              <h2>Data Migration Plan v1.0 - Sample</h2>
              <button
                className="modal-close"
                onClick={() => setShowPdfModal(false)}
              >
                &times;
              </button>
            </div>
            <div style={{ height: 'calc(100% - 60px)', width: '100%' }}>
              <iframe
                src={sampleDocumentAPI.downloadSamplePlan()}
                title="Sample Data Migration Plan"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Manage Collaborators Modal */}
      {managingProject && (
        <ManageCollaborators
          project={managingProject}
          currentUser={currentUser}
          onClose={() => setManagingProject(null)}
          onUpdate={() => {
            fetchProjects();
            fetchPendingInvites();
          }}
        />
      )}
    </div>
  );
};

export default ProjectsDashboard;
